<template>
    <v-dialog v-model="isOpen" persistent attach width="92vw" max-width="1200" scrollable>
        <v-card class="offer-preset-wizard">
            <header class="offer-preset-wizard__header">
                <h2 class="offer-preset-wizard__title">
                    {{
                        toSentenceCase(
                            $t(
                                isEditing
                                    ? 'admin.offerPresets.wizard.editTitle'
                                    : 'admin.offerPresets.wizard.newTitle'
                            )
                        )
                    }}
                </h2>
                <ol class="offer-preset-wizard__steps">
                    <li
                        v-for="(item, index) in steps"
                        :key="item.id"
                        class="offer-preset-wizard__step"
                        :class="{
                            'offer-preset-wizard__step--active': step === index + 1,
                            'offer-preset-wizard__step--complete': step > index + 1,
                        }"
                        :aria-current="step === index + 1 ? 'step' : undefined"
                    >
                        <button
                            type="button"
                            class="offer-preset-wizard__step-button"
                            :disabled="step <= index + 1"
                            @click="step = index + 1"
                        >
                            <span class="offer-preset-wizard__step-index">
                                <v-icon v-if="step > index + 1" size="16">mdi-check</v-icon>
                                <template v-else>{{ index + 1 }}</template>
                            </span>
                            <span class="offer-preset-wizard__step-text">
                                <span class="offer-preset-wizard__step-title">{{
                                    item.title
                                }}</span>
                                <span class="offer-preset-wizard__step-hint">{{ item.hint }}</span>
                            </span>
                        </button>
                    </li>
                </ol>
            </header>

            <v-card-text class="offer-preset-wizard__body">
                <section v-if="step === 1" class="offer-preset-wizard__section">
                    <p class="offer-preset-wizard__lead">{{ currentStep.description }}</p>
                    <div class="offer-preset-wizard__sources" role="radiogroup">
                        <button
                            v-for="source in availableSources"
                            :key="source.id"
                            type="button"
                            role="radio"
                            class="offer-preset-wizard__source"
                            :class="{
                                'offer-preset-wizard__source--selected':
                                    draft.creationSource === source.id,
                            }"
                            :aria-checked="draft.creationSource === source.id"
                            @click="draft.creationSource = source.id"
                        >
                            <v-icon class="offer-preset-wizard__source-icon" size="28">{{
                                source.icon
                            }}</v-icon>
                            <span class="offer-preset-wizard__source-title">{{
                                toSentenceCase($t(source.labelKey))
                            }}</span>
                            <span class="offer-preset-wizard__source-hint">{{
                                $t(source.hintKey)
                            }}</span>
                        </button>
                    </div>
                </section>

                <section v-else-if="step === 2" class="offer-preset-wizard__section">
                    <p class="offer-preset-wizard__lead">{{ currentStep.description }}</p>
                    <component
                        :is="configureComponent"
                        v-if="isOpen"
                        v-model="draft"
                        :price-tags="priceTags"
                        :presets="presets"
                        :context-source="contextSource"
                    />
                </section>

                <section v-else-if="step === 3" class="offer-preset-wizard__section">
                    <p class="offer-preset-wizard__lead">{{ currentStep.description }}</p>
                    <div class="offer-preset-wizard__form">
                        <div class="offer-preset-wizard__field offer-preset-wizard__field--wide">
                            <label class="offer-preset-wizard__label" for="offer-preset-name">
                                {{ toSentenceCase($t('admin.offerPresets.wizard.name')) }} *
                                <span class="offer-preset-wizard__count"
                                    >{{ (draft.name || '').length }}/80</span
                                >
                            </label>
                            <v-text-field
                                id="offer-preset-name"
                                v-model="draft.name"
                                variant="outlined"
                                density="comfortable"
                                maxlength="80"
                                :rules="[
                                    value =>
                                        !!value?.trim() ||
                                        toSentenceCase(
                                            $t('admin.offerPresets.wizard.nameRequired')
                                        ),
                                ]"
                            />
                        </div>
                        <div class="offer-preset-wizard__field offer-preset-wizard__field--wide">
                            <label
                                class="offer-preset-wizard__label"
                                for="offer-preset-description"
                            >
                                {{ toSentenceCase($t('admin.offerPresets.wizard.description')) }}
                                <span class="offer-preset-wizard__count"
                                    >{{ (draft.description || '').length }}/250</span
                                >
                            </label>
                            <v-textarea
                                id="offer-preset-description"
                                v-model="draft.description"
                                variant="outlined"
                                density="comfortable"
                                rows="3"
                                auto-grow
                                maxlength="250"
                                hide-details
                            />
                        </div>
                        <div class="offer-preset-wizard__field">
                            <label class="offer-preset-wizard__label" for="offer-preset-sort-order">
                                {{ toSentenceCase($t('admin.offerPresets.wizard.sortOrder')) }}
                            </label>
                            <v-text-field
                                id="offer-preset-sort-order"
                                v-model.number="draft.sortOrder"
                                variant="outlined"
                                density="comfortable"
                                type="number"
                                min="0"
                                :hint="$t('admin.offerPresets.wizard.sortOrderHint')"
                                persistent-hint
                            />
                        </div>
                        <div class="offer-preset-wizard__field">
                            <span class="offer-preset-wizard__label">
                                {{ toSentenceCase($t('admin.offerPresets.table.planner')) }}
                            </span>
                            <v-switch
                                v-model="isVisibleInPlanner"
                                color="primary"
                                density="comfortable"
                                inset
                                :label="
                                    toSentenceCase(
                                        $t(
                                            isVisibleInPlanner
                                                ? 'admin.offerPresets.wizard.visible'
                                                : 'admin.offerPresets.wizard.hidden'
                                        )
                                    )
                                "
                                :hint="$t('admin.offerPresets.wizard.plannerHint')"
                                persistent-hint
                            />
                        </div>
                    </div>
                </section>

                <section v-else class="offer-preset-wizard__section">
                    <p class="offer-preset-wizard__lead">{{ currentStep.description }}</p>
                    <div class="offer-preset-wizard__review">
                        <dl class="offer-preset-wizard__summary">
                            <dt>{{ toSentenceCase($t('admin.offerPresets.wizard.name')) }}</dt>
                            <dd>{{ draft.name }}</dd>
                            <dt>
                                {{ toSentenceCase($t('admin.offerPresets.wizard.description')) }}
                            </dt>
                            <dd>{{ draft.description || '—' }}</dd>
                            <dt>{{ toSentenceCase($t('admin.offerPresets.table.tag')) }}</dt>
                            <dd>{{ reviewTagLabel }}</dd>
                            <dt>
                                {{ toSentenceCase($t('admin.offerPresets.table.createdFrom')) }}
                            </dt>
                            <dd>
                                {{ activeSource ? toSentenceCase($t(activeSource.labelKey)) : '—' }}
                            </dd>
                            <dt>{{ toSentenceCase($t('admin.offerPresets.table.planner')) }}</dt>
                            <dd>
                                {{
                                    toSentenceCase(
                                        $t(
                                            draft.isHiddenInPlanner
                                                ? 'admin.offerPresets.wizard.hidden'
                                                : 'admin.offerPresets.wizard.visible'
                                        )
                                    )
                                }}
                            </dd>
                            <dt>{{ toSentenceCase($t('admin.offerPresets.wizard.sortOrder')) }}</dt>
                            <dd>{{ draft.sortOrder ?? 0 }}</dd>
                        </dl>
                        <div class="offer-preset-wizard__preview">
                            <span class="offer-preset-wizard__label">
                                {{ toSentenceCase($t('admin.offerPresets.actions.previewTag')) }}
                            </span>
                            <price-tag-renderer
                                v-if="reviewPriceTag && draft.templateStructure"
                                :layout="reviewPriceTag.layout"
                                :values="reviewValues"
                                :fields-by-key="reviewFieldsByKey"
                                :context="reviewContext"
                            />
                        </div>
                    </div>
                </section>
            </v-card-text>

            <footer class="offer-preset-wizard__footer">
                <v-btn variant="text" @click="close">
                    {{ toSentenceCase($t('admin.offerPresets.wizard.cancel')) }}
                </v-btn>
                <v-spacer />
                <span class="offer-preset-wizard__progress">
                    {{
                        $t('admin.offerPresets.wizard.stepOf', {
                            current: step,
                            total: steps.length,
                        })
                    }}
                </span>
                <v-btn v-if="step > 1" variant="outlined" color="primary" @click="step -= 1">
                    {{ toSentenceCase($t('admin.offerPresets.wizard.back')) }}
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    :disabled="!canContinue"
                    @click="continueOrSave"
                >
                    {{
                        toSentenceCase(
                            $t(
                                step === steps.length
                                    ? 'admin.offerPresets.wizard.save'
                                    : 'admin.offerPresets.wizard.next'
                            )
                        )
                    }}
                </v-btn>
            </footer>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { cloneDeep, get } from 'lodash';
import { useStore } from 'vuex';
import axios from 'axios';
import offerMechanicTemplates from '@enums/offer-mechanic-templates';
import i18n from '@/js/vue-i18n';
import creationSources from '../creation-sources';
import {
    readValuesFromMechanic,
    applyValuesToMechanic,
} from '@/js/utils/price-tags/binding-engine';
import { buildPriceTagContext } from '@/js/utils/price-tags/price-tag-context';
import { keyFields } from '@/js/utils/price-tags/validate-values';
import { toSentenceCase } from '@/js/utils/string-utils';
import { getOfferTemplateLabel, getPriceTagLabel } from '@/js/utils/offer-template-label';

const emit = defineEmits(['saved']);
const store = useStore();
const isOpen = ref(false);
const step = ref(1);
const editingId = ref(null);
const draft = ref({});
const steps = computed(() =>
    ['source', 'configure', 'details', 'review'].map(id => ({
        id,
        title: toSentenceCase(i18n.global.t(`admin.offerPresets.wizard.${id}`)),
        hint: i18n.global.t(`admin.offerPresets.wizard.steps.${id}.hint`),
        description: i18n.global.t(`admin.offerPresets.wizard.steps.${id}.description`),
    }))
);
const currentStep = computed(() => steps.value[step.value - 1]);
const isVisibleInPlanner = computed({
    get: () => !draft.value.isHiddenInPlanner,
    set: value => {
        draft.value.isHiddenInPlanner = !value;
    },
});
const priceTags = computed(() => store.state.priceTags.priceTags);
const presets = computed(() => store.state.offerMechanicPresets.offerMechanicPresets);
const defaultTagId = offerMechanicTemplates.newPricePerItem;
const isEditing = computed(() => !!editingId.value);
const activeSource = computed(() =>
    creationSources.find(source => source.id === draft.value.creationSource)
);
const availableSources = computed(() =>
    creationSources.filter(source => source.isEnabled(store.state.clientConfig.toggleLogic))
);
const getAvailableSource = source =>
    availableSources.value.find(item => item.id === source)?.id || availableSources.value[0]?.id;
const configureComponent = computed(
    () => activeSource.value?.configureComponent || 'configure-from-price-tag'
);
const contextSource = computed(() => {
    const loyaltyKey = get(
        store.state.clientConfig.generalConfig,
        'priceTags.loyaltyCustomerAvailabilityKey',
        null
    );
    return {
        products: [],
        productOfferGroups: [],
        startDate: null,
        endDate: null,
        moment: store.moment,
        currencySymbol: store.getters['clientConfig/currencySymbol'] || '',
        loyaltyKey,
        transformContext: {
            loyaltyKey,
            defaultCustomerAvailability: get(
                store.state.clientConfig.offerDefaults,
                'customerAvailability',
                null
            ),
            maxTierCount: get(
                store.state.clientConfig.generalConfig,
                'maxOfferMechanicTierCount',
                3
            ),
        },
    };
});
const reviewPriceTag = computed(
    () => store.getters['priceTags/priceTagsKeyed'][draft.value.priceTagId]
);
const reviewFields = computed(() => reviewPriceTag.value?.fields || []);
const reviewTagLabel = computed(() =>
    getPriceTagLabel({
        id: draft.value.priceTagId,
        name: reviewPriceTag.value?.name,
        currencySymbol: contextSource.value.currencySymbol,
    })
);
const reviewFieldsByKey = computed(() => keyFields(reviewFields.value));
const reviewValues = computed(() =>
    readValuesFromMechanic({
        offerMechanic: draft.value.templateStructure || {},
        fields: reviewFields.value,
        displayValues: draft.value.displayValues,
        transformContext: contextSource.value.transformContext,
    })
);
const reviewContext = computed(() => {
    const { offerMechanic, displayValues } = applyValuesToMechanic({
        template: draft.value.templateStructure || {},
        fields: reviewFields.value,
        values: reviewValues.value,
        transformContext: contextSource.value.transformContext,
    });
    return buildPriceTagContext({
        ...contextSource.value,
        offerMechanic,
        displayValues,
        useStoredPrices: false,
    });
});
const canContinue = computed(() => {
    if (step.value === 1) return !!draft.value.creationSource;
    if (step.value === 2) {
        return (
            !!draft.value.priceTagId &&
            !!draft.value.templateStructure &&
            draft.value.isValid !== false
        );
    }
    if (step.value === 3) return !!draft.value.name?.trim();
    return true;
});

watch(
    () => draft.value.creationSource,
    source => {
        if (source === 'mechanicForm') {
            if (draft.value.source !== 'system') draft.value.priceTagId = defaultTagId;
            draft.value.displayValues = draft.value.displayValues || {};
        }
    }
);

const open = ({ preset = null, source = 'priceTag' } = {}) => {
    editingId.value = preset?._id || null;
    step.value = 1;
    const requestedSource = preset?.creationSource === 'mechanicForm' ? 'mechanicForm' : source;
    const preserveExistingSource = !!editingId.value && preset?.source !== 'system';
    const availableSource = preserveExistingSource
        ? requestedSource
        : getAvailableSource(requestedSource);
    draft.value = preset
        ? {
              ...cloneDeep(preset),
              // System presets have no authoring source, so the admin picks how to edit them.
              creationSource: availableSource,
              // System presets are stored with their key as name; start from the readable title.
              name: getOfferTemplateLabel({
                  key: preset.offerTemplate,
                  name: preset.name,
                  currencySymbol: contextSource.value.currencySymbol,
              }),
              tagValues: undefined,
          }
        : {
              creationSource: availableSource,
              name: '',
              description: '',
              priceTagId: availableSource === 'mechanicForm' ? defaultTagId : null,
              displayValues: {},
              templateStructure: null,
              isHiddenInPlanner: false,
              sortOrder: 0,
          };
    isOpen.value = true;
};

const close = () => {
    isOpen.value = false;
    step.value = 1;
};

const continueOrSave = async () => {
    if (step.value < 4) {
        step.value += 1;
        return;
    }
    const payload = cloneDeep(draft.value);
    delete payload._id;
    delete payload.tagValues;
    delete payload.isValid;
    const source = creationSources.find(item => item.id === payload.creationSource);
    if (!editingId.value && source) Object.assign(payload, source.buildPayload(payload));
    if (editingId.value) {
        await axios.patch(`/api/offer-mechanic-presets/${editingId.value}`, payload);
    } else {
        await axios.post('/api/offer-mechanic-presets', payload);
    }
    await store.dispatch('offerMechanicPresets/fetchOfferMechanicPresets');
    emit('saved');
    close();
};

defineExpose({ open });
</script>

<style scoped lang="scss">
$wizard-accent: #2f477c;
$wizard-muted: #646b73;
$wizard-border: #dfe3e8;

.offer-preset-wizard {
    font-size: 1.4rem;
    line-height: 1.5;
    color: #1f2933;

    &__header {
        padding: 2.4rem 3.2rem 0;
        border-bottom: 1px solid $wizard-border;
    }

    &__title {
        margin: 0 0 2rem;
        font-size: 2.2rem;
        font-weight: 600;
        line-height: 1.3;
    }

    &__steps {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        gap: 1.6rem;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__step {
        position: relative;
        padding-bottom: 1.6rem;
        border-bottom: 3px solid transparent;

        &--active {
            border-bottom-color: $wizard-accent;
        }

        &--complete {
            border-bottom-color: rgba($wizard-accent, 0.35);
        }
    }

    &__step-button {
        display: flex;
        gap: 1.2rem;
        align-items: flex-start;
        width: 100%;
        padding: 0;
        text-align: left;
        background: none;
        border: 0;
        cursor: pointer;

        &:disabled {
            cursor: default;
        }

        &:focus-visible {
            outline: 2px solid $wizard-accent;
            outline-offset: 4px;
        }
    }

    &__step-index {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        width: 2.8rem;
        height: 2.8rem;
        font-size: 1.3rem;
        font-weight: 600;
        color: $wizard-muted;
        border: 2px solid $wizard-border;
        border-radius: 50%;

        .offer-preset-wizard__step--active & {
            color: #fff;
            background-color: $wizard-accent;
            border-color: $wizard-accent;
        }

        .offer-preset-wizard__step--complete & {
            color: $wizard-accent;
            border-color: $wizard-accent;
        }
    }

    &__step-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    &__step-title {
        font-size: 1.5rem;
        font-weight: 600;
        color: $wizard-muted;

        .offer-preset-wizard__step--active &,
        .offer-preset-wizard__step--complete & {
            color: $wizard-accent;
        }
    }

    &__step-hint {
        font-size: 1.25rem;
        color: $wizard-muted;
    }

    &__body {
        min-height: 44rem;
        max-height: 68vh;
        padding: 2.4rem 3.2rem !important;
        font-size: 1.4rem !important;
    }

    &__section {
        display: flex;
        flex-direction: column;
        gap: 2rem;
    }

    &__lead {
        margin: 0;
        font-size: 1.5rem;
        color: $wizard-muted;
    }

    &__sources {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(28rem, 1fr));
        gap: 1.6rem;
    }

    &__source {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        padding: 2rem;
        text-align: left;
        background: #fff;
        border: 1px solid $wizard-border;
        border-radius: 0.8rem;
        cursor: pointer;
        transition: border-color 0.15s, box-shadow 0.15s;

        &:hover {
            border-color: rgba($wizard-accent, 0.5);
        }

        &:focus-visible {
            outline: 2px solid $wizard-accent;
            outline-offset: 2px;
        }

        &--selected {
            border-color: $wizard-accent;
            box-shadow: 0 0 0 1px $wizard-accent;
            background-color: rgba($wizard-accent, 0.04);
        }
    }

    &__source-icon {
        color: $wizard-accent;
    }

    &__source-title {
        font-size: 1.6rem;
        font-weight: 600;
    }

    &__source-hint {
        color: $wizard-muted;
    }

    &__form {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 0.8rem 2.4rem;
        max-width: 80rem;
    }

    &__field {
        display: flex;
        flex-direction: column;
        gap: 0.6rem;

        &--wide {
            grid-column: 1 / -1;
        }
    }

    &__label {
        display: flex;
        justify-content: space-between;
        font-size: 1.3rem;
        font-weight: 600;
    }

    &__count {
        font-weight: 400;
        color: $wizard-muted;
    }

    &__review {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
        gap: 3.2rem;
        align-items: start;
    }

    &__summary {
        display: grid;
        grid-template-columns: max-content 1fr;
        gap: 1rem 2.4rem;
        margin: 0;

        dt {
            font-weight: 600;
            color: $wizard-muted;
        }

        dd {
            margin: 0;
            word-break: break-word;
        }
    }

    &__preview {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        align-items: flex-start;
    }

    &__footer {
        display: flex;
        gap: 1.2rem;
        align-items: center;
        padding: 1.6rem 3.2rem;
        border-top: 1px solid $wizard-border;
    }

    &__progress {
        margin-right: 0.8rem;
        font-size: 1.3rem;
        color: $wizard-muted;
    }

    :deep(.v-btn) {
        font-size: 1.4rem;
        letter-spacing: normal;
        text-transform: none;
    }

    :deep(.v-field),
    :deep(.v-label),
    :deep(.v-messages) {
        font-size: 1.3rem;
    }

    :deep(.v-field__input) {
        font-size: 1.4rem;
    }

    // Scoped to outlined fields so the planner's offer-mechanic controls keep their own styling.
    // The legacy Vuetify 2 sheet sets `.v-text-field input { padding: 8px 0 }` and `.v-input { font-size: 1.2rem }`.
    :deep(.v-input:has(.v-field--variant-outlined)) {
        font-size: 1.6rem;
    }

    :deep(.v-field--variant-outlined) {
        font-size: 1.6rem;

        input.v-field__input,
        textarea.v-field__input,
        div.v-field__input {
            min-height: 4.4rem;
            padding: 1rem 1.4rem;
            font-size: 1.6rem;
            line-height: 2.4rem;
        }

        .v-select__selection-text {
            font-size: 1.6rem;
        }

        .v-field__prepend-inner,
        .v-field__append-inner,
        .v-field__clearable {
            align-items: center;
            padding-top: 0;
        }
    }

    :deep(.v-input__details) {
        padding-inline: 1.4rem;
    }
}
</style>
